import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, NavLink, Routes, Route, Navigate } from "react-router-dom";
import { adminApi, fileUrl } from "../lib/adminApi";
import { experiences, apartments, wines as staticWines } from "../data/site";
import { LogOut, RefreshCw, Trash2, Mail, MailCheck, MailX, Upload, RotateCw, ArrowUp, ArrowDown, Pencil, X, Image as ImageIcon } from "lucide-react";
import { IMAGE_SLOTS } from "../lib/imageSlots";

function useAdminSession() {
  const [state, setState] = useState({ loading: true, ok: false });
  const nav = useNavigate();
  useEffect(() => {
    adminApi.get("/session").then((r) => {
      if (!r.data.authenticated) nav("/admin/login");
      else setState({ loading: false, ok: true });
    }).catch(() => nav("/admin/login"));
  }, [nav]);
  return state;
}

export default function Admin() {
  const s = useAdminSession();
  if (s.loading) return <div className="min-h-screen flex items-center justify-center text-charcoal/50">Načítání…</div>;
  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <Routes>
          <Route index element={<EnquiriesTab />} />
          <Route path="fotky" element={<PhotosTab />} />
          <Route path="vina" element={<WinesTab />} />
          <Route path="obsazenost" element={<AvailabilityTab />} />
          <Route path="ical" element={<IcalTab />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function AdminHeader() {
  const nav = useNavigate();
  const logout = async () => { try { await adminApi.post("/logout"); } catch (_) {} nav("/admin/login"); };
  const link = ({ isActive }) => `px-4 py-2 text-sm tracking-wide ${isActive ? "bg-burgundy text-ivory" : "text-charcoal hover:bg-cream border border-transparent hover:border-charcoal/15"}`;
  return (
    <header className="bg-ivory border-b border-charcoal/10 sticky top-0 z-30">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[.3em] uppercase text-burgundy">Administrace</div>
          <div className="font-serif text-lg">Vinařství Brychta</div>
        </div>
        <nav className="flex gap-1 flex-wrap" aria-label="Admin menu">
          <NavLink end to="/admin" className={link} data-testid="admin-tab-poptavky">Poptávky</NavLink>
          <NavLink to="/admin/fotky" className={link} data-testid="admin-tab-fotky">Fotky</NavLink>
          <NavLink to="/admin/vina" className={link} data-testid="admin-tab-vina">Vína</NavLink>
          <NavLink to="/admin/obsazenost" className={link} data-testid="admin-tab-obsazenost">Obsazenost</NavLink>
          <NavLink to="/admin/ical" className={link} data-testid="admin-tab-ical">iCal</NavLink>
        </nav>
        <button onClick={logout} className="text-sm text-charcoal/70 hover:text-burgundy inline-flex items-center gap-2" data-testid="admin-logout">
          <LogOut size={14} /> Odhlásit
        </button>
      </div>
    </header>
  );
}

// ---------------------- Enquiries ----------------------
function EnquiriesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.get("/enquiries"); setList(r.data.enquiries || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    try { await adminApi.post(`/enquiries/${id}/retry-email`); await load(); }
    catch (_) {}
  };
  const markRead = async (id) => { await adminApi.post(`/enquiries/${id}/mark-read`); await load(); };

  return (
    <section className="py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-3xl">Poptávky</h1>
        <button onClick={load} className="text-sm inline-flex items-center gap-2 border border-charcoal/20 px-3 py-2 hover:bg-ivory" data-testid="admin-refresh">
          <RefreshCw size={14} /> Obnovit
        </button>
      </div>
      {loading ? <div>Načítání…</div> : list.length === 0 ? (
        <div className="text-charcoal/60">Žádné poptávky.</div>
      ) : (
        <div className="grid gap-3">
          {list.map((e) => (
            <article key={e.id} className={`bg-ivory border p-5 ${e.read_at ? "border-charcoal/10" : "border-burgundy/30"}`} data-testid={`enquiry-row-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] tracking-[.2em] uppercase text-burgundy">{e.type}</div>
                  <div className="font-serif text-lg mt-1">{e.name}</div>
                  <div className="text-sm text-charcoal/70 mt-0.5">
                    <a href={`mailto:${e.email}`} className="hover:text-burgundy">{e.email}</a> · {e.phone}
                  </div>
                  <div className="text-xs text-charcoal/50 mt-1">{new Date(e.created_at).toLocaleString("cs-CZ")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <EmailStatus e={e} onRetry={() => retry(e.id)} />
                  {!e.read_at && (
                    <button onClick={() => markRead(e.id)} className="text-xs border border-charcoal/20 px-3 py-1.5 hover:bg-cream">Označit jako přečtené</button>
                  )}
                </div>
              </div>
              {(e.message || e.arrival_date || e.wines) && (
                <div className="mt-3 pt-3 border-t border-charcoal/10 grid md:grid-cols-2 gap-3 text-sm">
                  {e.arrival_date && <div><span className="text-charcoal/55">Termín: </span>{e.arrival_date} → {e.departure_date || "—"}</div>}
                  {e.apartment_preference && <div><span className="text-charcoal/55">Apartmán: </span>{e.apartment_preference}</div>}
                  {e.experience_slug && <div><span className="text-charcoal/55">Zážitek: </span>{e.experience_slug}</div>}
                  {(e.adults || e.children) && <div><span className="text-charcoal/55">Hosté: </span>{e.adults || 0} dosp. {e.children ? `+ ${e.children} dětí` : ""}</div>}
                  {e.wines?.length > 0 && <div className="md:col-span-2"><span className="text-charcoal/55">Víno: </span>{e.wines.map((w) => `${w.name} ×${w.quantity}`).join(", ")}</div>}
                  {e.message && <div className="md:col-span-2 bg-cream border-l-2 border-burgundy p-3 whitespace-pre-wrap">{e.message}</div>}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function EmailStatus({ e, onRetry }) {
  if (e.email_status === "sent") return <span className="inline-flex items-center gap-1.5 text-xs bg-vineyard/10 text-vineyard px-2.5 py-1.5"><MailCheck size={13} /> E-mail odeslán</span>;
  if (e.email_status === "failed") return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-1.5 text-xs bg-burgundy/10 text-burgundy px-2.5 py-1.5" title={e.email_error || ""}><MailX size={13} /> E-mail neodeslán</span>
      <button onClick={onRetry} className="text-xs inline-flex items-center gap-1 border border-charcoal/20 px-2.5 py-1.5 hover:bg-cream"><RotateCw size={12} /> Opakovat</button>
    </div>
  );
  return <span className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-gold px-2.5 py-1.5"><Mail size={13} /> Čeká</span>;
}

// ---------------------- Photos ----------------------
const TARGET_TYPES = [
  { value: "apartment", label: "Apartmán" },
  { value: "experience", label: "Zážitek" },
  { value: "wine", label: "Víno – hlavní fotografie" },
  { value: "wine-detail", label: "Víno – druhá fotografie" },
  { value: "experience-gallery", label: "Řízená degustace – galerie" },
  { value: "gallery", label: "Galerie" },
  { value: "hero", label: "Hero" },
  { value: "story", label: "Rodinný příběh" },
];

// Public gallery subcategories – kept in sync with lib/photos.js
const GALLERY_SUBCATS = [
  { value: "vinohrad", label: "Vinohrad" },
  { value: "degustace", label: "Degustace" },
  { value: "ubytovani", label: "Ubytování" },
  { value: "nase-vina", label: "Naše vína" },
  { value: "vinarstvi", label: "Vinařství" },
  { value: "zazitky", label: "Zážitky" },
];

const WINE_OPTIONS = staticWines.map((w) => ({ value: w.slug, label: w.name }));
const TASTING_GALLERY_OPTIONS = [1, 2, 3, 4].map((n) => ({ value: `rizena-degustace-${n}`, label: `Doplňková fotografie ${n}` }));

const ACCEPT = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_MB = 15;

function PhotosTab() {
  const [list, setList] = useState([]);
  const [targetType, setTargetType] = useState("apartment");
  const [targetSlug, setTargetSlug] = useState(apartments[0]?.slug || "");
  const [alt, setAlt] = useState("");
  const [uploads, setUploads] = useState([]); // {name, progress, status, error}
  const [dragOver, setDragOver] = useState(false);
  const [filter, setFilter] = useState({ type: "", slug: "" });
  const [editing, setEditing] = useState(null); // photo currently in editor modal

  const load = useCallback(async () => {
    const r = await adminApi.get("/photos");
    setList(r.data.photos || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const slugOptions = targetType === "apartment" ? apartments.map((a) => ({ value: a.slug, label: a.name }))
    : targetType === "experience" ? experiences.map((e) => ({ value: e.slug, label: e.title }))
    : targetType === "wine" || targetType === "wine-detail" ? WINE_OPTIONS
    : targetType === "experience-gallery" ? TASTING_GALLERY_OPTIONS
    : targetType === "gallery" ? GALLERY_SUBCATS
    : [];

  const slugFieldLabel = targetType === "gallery" ? "Podkategorie" : "Přiřadit k";

  const validateFiles = (files) => {
    const arr = Array.from(files);
    return arr.filter((f) => {
      if (!ACCEPT.includes(f.type)) return false;
      if (f.size > MAX_MB * 1024 * 1024) return false;
      return true;
    });
  };

  const doUpload = async (files) => {
    const valid = validateFiles(files);
    if (valid.length === 0) return;
    // Determine next order based on current published photos in target
    const same = list.filter((p) => p.target_type === targetType && p.target_slug === targetSlug);
    const nextOrder = same.length ? Math.max(...same.map((p) => p.order || 0)) + 1 : 0;

    // Prepare progress rows
    const rows = valid.map((f, i) => ({ id: `${Date.now()}-${i}`, name: f.name, progress: 0, status: "uploading", error: null }));
    setUploads((u) => [...rows, ...u]);

    for (let i = 0; i < valid.length; i++) {
      const f = valid[i];
      const rowId = rows[i].id;
      const fd = new FormData();
      fd.append("file", f);
      fd.append("target_type", targetType);
      fd.append("target_slug", targetSlug);
      fd.append("alt", alt);
      fd.append("order", String(nextOrder + i));
      try {
        await adminApi.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const p = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setUploads((u) => u.map((r) => (r.id === rowId ? { ...r, progress: p } : r)));
          },
        });
        setUploads((u) => u.map((r) => (r.id === rowId ? { ...r, progress: 100, status: "done" } : r)));
      } catch (err) {
        setUploads((u) => u.map((r) => (r.id === rowId ? { ...r, status: "error", error: err?.response?.data?.detail || "Chyba" } : r)));
      }
    }
    await load();
    // Clear finished rows after 2s
    setTimeout(() => setUploads((u) => u.filter((r) => r.status === "error")), 2000);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) doUpload(e.dataTransfer.files);
  };

  const remove = async (id) => {
    if (!window.confirm("Smazat fotku?")) return;
    await adminApi.delete(`/photos/${id}`);
    await load();
  };

  const togglePublish = async (p) => {
    await adminApi.patch(`/photos/${p.id}`, {
      target_type: p.target_type, target_slug: p.target_slug,
      alt: p.alt || "", order: p.order || 0, published: !p.published,
    });
    await load();
  };

  const move = async (p, direction) => {
    // Reorder within same target group.
    const group = list
      .filter((x) => x.target_type === p.target_type && x.target_slug === p.target_slug)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = group.findIndex((x) => x.id === p.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= group.length) return;
    const a = group[idx], b = group[swapIdx];
    await Promise.all([
      adminApi.patch(`/photos/${a.id}`, { target_type: a.target_type, target_slug: a.target_slug, alt: a.alt || "", order: b.order || 0, published: a.published }),
      adminApi.patch(`/photos/${b.id}`, { target_type: b.target_type, target_slug: b.target_slug, alt: b.alt || "", order: a.order || 0, published: b.published }),
    ]);
    await load();
  };

  const filtered = list.filter((p) => (!filter.type || p.target_type === filter.type) && (!filter.slug || p.target_slug === filter.slug))
    .sort((a, b) => (a.target_type + a.target_slug).localeCompare(b.target_type + b.target_slug) || (a.order || 0) - (b.order || 0));

  return (
    <section className="py-8">
      <h1 className="font-serif text-3xl mb-4">Fotky</h1>

      {/* SLOTS – every predefined content-image position, always visible */}
      <SlotsPanel list={list} onChanged={load} />

      {/* Drag & drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed p-8 md:p-10 text-center transition-colors ${dragOver ? "border-burgundy bg-cream" : "border-charcoal/25 bg-ivory"}`}
        data-testid="photo-dropzone"
      >
        <Upload className="mx-auto text-burgundy" size={28} />
        <div className="font-serif text-xl mt-3">Přetáhněte sem soubory</div>
        <div className="text-sm text-charcoal/70 mt-1">JPG, PNG nebo WEBP · max {MAX_MB} MB · více souborů najednou</div>

        <div className="mt-6 grid md:grid-cols-3 gap-3 max-w-3xl mx-auto text-left">
          <label>
            <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Kategorie</span>
            <select value={targetType} onChange={(e) => { setTargetType(e.target.value); setTargetSlug(""); }} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm bg-white" data-testid="photo-target-type">
              {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          {slugOptions.length > 0 && (
            <label>
              <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">{slugFieldLabel}</span>
              <select value={targetSlug} onChange={(e) => setTargetSlug(e.target.value)} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm bg-white" data-testid="photo-target-slug">
                <option value="">— vyberte —</option>
                {slugOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          )}
          <label className={slugOptions.length === 0 ? "md:col-span-2" : ""}>
            <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Alt text (nepovinný)</span>
            <input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Popis obrázku" className="w-full border border-charcoal/25 px-3 py-2.5 text-sm bg-white" />
          </label>
        </div>

        <div className="mt-5">
          <label className="inline-block bg-burgundy hover:bg-burgundy-hover text-ivory px-6 py-2.5 tracking-wide cursor-pointer text-sm">
            Vybrat soubory
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => doUpload(e.target.files)} data-testid="photo-file" />
          </label>
        </div>
      </div>

      {/* Upload progress */}
      {uploads.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="upload-progress">
          {uploads.map((u) => (
            <div key={u.id} className="bg-ivory border border-charcoal/10 p-3">
              <div className="flex justify-between text-sm">
                <span className="truncate">{u.name}</span>
                <span className={u.status === "error" ? "text-burgundy" : "text-charcoal/70"}>
                  {u.status === "error" ? (u.error || "Chyba") : `${u.progress}%`}
                </span>
              </div>
              <div className="mt-1.5 h-1 bg-cream overflow-hidden">
                <div className={`h-full ${u.status === "error" ? "bg-burgundy/60" : "bg-vineyard"}`} style={{ width: `${u.progress}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="mt-8 flex flex-wrap items-center gap-2">
        <span className="text-[11px] tracking-[.2em] uppercase text-charcoal/60">Filtr:</span>
        <select value={filter.type} onChange={(e) => setFilter({ type: e.target.value, slug: "" })} className="border border-charcoal/20 px-3 py-1.5 text-sm bg-white">
          <option value="">Všechny kategorie</option>
          {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {(filter.type === "apartment" || filter.type === "experience") && (
          <select value={filter.slug} onChange={(e) => setFilter({ ...filter, slug: e.target.value })} className="border border-charcoal/20 px-3 py-1.5 text-sm bg-white">
            <option value="">Všechny položky</option>
            {(filter.type === "apartment" ? apartments.map((a) => ({ v: a.slug, l: a.name })) : experiences.map((x) => ({ v: x.slug, l: x.title }))).map((o) => (
              <option key={o.v} value={o.v}>{o.l}</option>
            ))}
          </select>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {filtered.map((p) => (
          <div key={p.id} className="bg-ivory border border-charcoal/10 flex flex-col" data-testid={`photo-${p.id}`}>
            <div className="relative aspect-[4/3] overflow-hidden bg-cream">
              <img src={fileUrl(p.id)} alt={p.alt || ""} className={`w-full h-full object-cover ${!p.published ? "opacity-40" : ""}`} loading="lazy" />
              {!p.published && (
                <span className="absolute top-2 left-2 bg-charcoal/80 text-ivory text-[10px] tracking-[.2em] uppercase px-2 py-1">Skryté</span>
              )}
            </div>
            <div className="p-3 text-xs flex-1">
              <div className="text-charcoal/60">{p.target_type}{p.target_slug ? ` · ${p.target_slug}` : ""}</div>
              <div className="mt-1 truncate">{p.alt || <span className="italic text-charcoal/40">bez alt textu</span>}</div>
            </div>
            <div className="flex justify-between items-center border-t border-charcoal/10 px-2 py-1.5">
              <div className="flex gap-1">
                <button onClick={() => move(p, "up")} className="p-1.5 hover:bg-cream text-charcoal/60 hover:text-burgundy" aria-label="Posunout nahoru" data-testid={`photo-up-${p.id}`}><ArrowUp size={14} /></button>
                <button onClick={() => move(p, "down")} className="p-1.5 hover:bg-cream text-charcoal/60 hover:text-burgundy" aria-label="Posunout dolů" data-testid={`photo-down-${p.id}`}><ArrowDown size={14} /></button>
                <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-cream text-charcoal/60 hover:text-burgundy" aria-label="Upravit" data-testid={`photo-edit-${p.id}`}><Pencil size={14} /></button>
              </div>
              <div className="flex gap-2 items-center">
                <button onClick={() => togglePublish(p)} className="text-[11px] underline hover:text-burgundy" data-testid={`photo-toggle-${p.id}`}>{p.published ? "Skrýt" : "Zveřejnit"}</button>
                <button onClick={() => remove(p.id)} className="text-charcoal/50 hover:text-burgundy p-1" aria-label="Smazat" data-testid={`photo-delete-${p.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-charcoal/60 py-8 text-center">Zatím žádné fotky.</div>}
      </div>

      {editing && (
        <PhotoEditor photo={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
      )}
    </section>
  );
}

function PhotoEditor({ photo, onClose, onSaved }) {
  const [targetType, setTargetType] = useState(photo.target_type);
  const [targetSlug, setTargetSlug] = useState(photo.target_slug || "");
  const [alt, setAlt] = useState(photo.alt || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const slugOptions = targetType === "apartment" ? apartments.map((a) => ({ value: a.slug, label: a.name }))
    : targetType === "experience" ? experiences.map((e) => ({ value: e.slug, label: e.title }))
    : targetType === "wine" || targetType === "wine-detail" ? WINE_OPTIONS
    : targetType === "experience-gallery" ? TASTING_GALLERY_OPTIONS
    : targetType === "gallery" ? GALLERY_SUBCATS
    : [];

  const slugFieldLabel = targetType === "gallery" ? "Podkategorie" : "Přiřadit k";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await adminApi.patch(`/photos/${photo.id}`, {
        target_type: targetType, target_slug: targetSlug,
        alt, order: photo.order || 0, published: !!photo.published,
      });
      await onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Uložení selhalo."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60" onClick={onClose} data-testid="photo-editor">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-ivory w-full max-w-lg p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Upravit fotku</h3>
          <button type="button" onClick={onClose} aria-label="Zavřít" className="p-1 hover:bg-cream"><X size={18} /></button>
        </div>
        <img src={fileUrl(photo.id)} alt="" className="w-full aspect-[4/3] object-cover" />
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <label>
            <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Kategorie</span>
            <select value={targetType} onChange={(e) => { setTargetType(e.target.value); setTargetSlug(""); }} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm">
              {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          {slugOptions.length > 0 && (
            <label>
              <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">{targetType === "gallery" ? "Podkategorie" : "Přiřadit k"}</span>
              <select value={targetSlug} onChange={(e) => setTargetSlug(e.target.value)} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm">
                <option value="">— vyberte —</option>
                {slugOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          )}
        </div>
        <label className="block mt-3">
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Alt text</span>
          <input value={alt} onChange={(e) => setAlt(e.target.value)} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm" data-testid="photo-editor-alt" />
        </label>
        {err && <div className="mt-3 text-sm text-red-700">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-charcoal/25 px-4 py-2 text-sm">Zrušit</button>
          <button disabled={saving} type="submit" className="bg-burgundy hover:bg-burgundy-hover text-ivory px-5 py-2 text-sm" data-testid="photo-editor-save">
            {saving ? "Ukládám…" : "Uložit"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------- Availability ----------------------
function AvailabilityTab() {
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({ apartment_slug: apartments[0].slug, start: "", end: "", note: "" });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const r = await adminApi.get("/availability");
    setBlocks(r.data.blocks || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault(); setError("");
    try { await adminApi.post("/availability", form); setForm({ ...form, start: "", end: "", note: "" }); await load(); }
    catch (err) { setError(err?.response?.data?.detail || "Uložení selhalo."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Smazat blok?")) return;
    try { await adminApi.delete(`/availability/${id}`); await load(); }
    catch (err) { alert(err?.response?.data?.detail || "Chyba"); }
  };

  return (
    <section className="py-8">
      <h1 className="font-serif text-3xl mb-4">Obsazenost</h1>

      <form onSubmit={add} className="bg-ivory border border-charcoal/10 p-5 md:p-6 grid md:grid-cols-5 gap-3 items-end" data-testid="block-form">
        <label>
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Apartmán</span>
          <select value={form.apartment_slug} onChange={(e) => setForm({ ...form, apartment_slug: e.target.value })} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm">
            {apartments.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
          </select>
        </label>
        <label>
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Od</span>
          <input required type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm" data-testid="block-start" />
        </label>
        <label>
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Do</span>
          <input required type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm" data-testid="block-end" />
        </label>
        <label className="md:col-span-2">
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Poznámka</span>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm" />
        </label>
        {error && <div className="md:col-span-5 text-sm text-red-700">{error}</div>}
        <button className="md:col-span-5 bg-burgundy hover:bg-burgundy-hover text-ivory py-3 tracking-wide" data-testid="block-submit">Přidat blokaci</button>
      </form>

      <div className="mt-8 grid gap-2">
        {blocks.length === 0 && <div className="text-charcoal/60">Zatím žádné bloky.</div>}
        {blocks.map((b) => (
          <div key={b.id || b.start} className="bg-ivory border border-charcoal/10 p-4 flex flex-wrap justify-between items-center gap-3">
            <div>
              <div className="text-sm">
                <span className="font-medium">{apartments.find((a) => a.slug === b.apartment_slug)?.name || b.apartment_slug}</span>
                <span className="text-charcoal/60"> · {b.start} → {b.end}</span>
              </div>
              <div className="text-xs text-charcoal/50 mt-0.5">{b.source} {b.note ? `· ${b.note}` : ""}</div>
            </div>
            {b.source === "manual" && (
              <button onClick={() => remove(b.id)} className="text-charcoal/50 hover:text-burgundy" aria-label="Smazat"><Trash2 size={14} /></button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------- iCal ----------------------
function IcalTab() {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ apartment_slug: apartments[0].slug, url: "", label: "" });
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const r = await adminApi.get("/ical");
    setSources(r.data.sources || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    await adminApi.post("/ical", form);
    setForm({ ...form, url: "", label: "" });
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm("Smazat zdroj a jeho importované rezervace?")) return;
    await adminApi.delete(`/ical/${id}`);
    await load();
  };
  const sync = async (id) => {
    setSyncing(true);
    try { await adminApi.post(`/ical/${id}/sync`); } finally { setSyncing(false); await load(); }
  };
  const syncAll = async () => {
    setSyncing(true);
    try { await adminApi.post("/ical/sync-all"); } finally { setSyncing(false); await load(); }
  };

  return (
    <section className="py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-3xl">iCal synchronizace</h1>
        <button onClick={syncAll} disabled={syncing} className="text-sm border border-charcoal/20 px-3 py-2 hover:bg-ivory inline-flex items-center gap-2 disabled:opacity-60" data-testid="ical-sync-all">
          <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Synchronizuji…" : "Synchronizovat vše"}
        </button>
      </div>

      <p className="text-sm text-charcoal/70 mb-6 max-w-2xl">
        Vložte adresu ICS souboru z Google Kalendáře, Booking.com nebo Airbnb pro každý apartmán zvlášť. Importované rezervace se zobrazí jako obsazené,
        na veřejném webu se nikdy nezobrazí jména hostů.
      </p>

      <form onSubmit={add} className="bg-ivory border border-charcoal/10 p-5 md:p-6 grid md:grid-cols-5 gap-3 items-end" data-testid="ical-form">
        <label>
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Apartmán</span>
          <select value={form.apartment_slug} onChange={(e) => setForm({ ...form, apartment_slug: e.target.value })} className="w-full border border-charcoal/25 px-3 py-2.5 text-sm">
            {apartments.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
          </select>
        </label>
        <label className="md:col-span-3">
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">ICS URL</span>
          <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…/basic.ics" className="w-full border border-charcoal/25 px-3 py-2.5 text-sm" data-testid="ical-url" />
        </label>
        <label>
          <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">Popisek</span>
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Booking" className="w-full border border-charcoal/25 px-3 py-2.5 text-sm" />
        </label>
        <button className="md:col-span-5 bg-burgundy hover:bg-burgundy-hover text-ivory py-3 tracking-wide" data-testid="ical-add">Přidat zdroj</button>
      </form>

      <div className="mt-8 grid gap-3">
        {sources.length === 0 && <div className="text-charcoal/60">Žádné iCal zdroje.</div>}
        {sources.map((s) => (
          <div key={s.id} className="bg-ivory border border-charcoal/10 p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-medium">{apartments.find((a) => a.slug === s.apartment_slug)?.name || s.apartment_slug} {s.label ? `· ${s.label}` : ""}</div>
                <div className="text-xs text-charcoal/50 truncate mt-1">{s.url}</div>
                <div className="text-xs mt-2">
                  {s.last_error ? <span className="text-burgundy">Chyba: {s.last_error}</span> :
                    s.last_synced_at ? <span className="text-charcoal/60">Poslední synchronizace: {new Date(s.last_synced_at).toLocaleString("cs-CZ")}</span> :
                    <span className="text-charcoal/50">Ještě nesynchronizováno</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => sync(s.id)} className="text-sm border border-charcoal/20 px-3 py-1.5 hover:bg-cream inline-flex items-center gap-1.5" data-testid={`ical-sync-${s.id}`}>
                  <RefreshCw size={13} /> Synchronizovat
                </button>
                <button onClick={() => remove(s.id)} className="text-charcoal/50 hover:text-burgundy" aria-label="Smazat"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------- Slots panel ----------------------
function SlotsPanel({ list, onChanged }) {
  const bySlot = (t, s) => list.filter((p) => p.target_type === t && p.target_slug === s && !p.is_deleted)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const upload = async (slot, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("target_type", slot.target_type);
    fd.append("target_slug", slot.target_slug);
    fd.append("alt", slot.label);
    fd.append("order", "0");
    try { await adminApi.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } }); await onChanged(); }
    catch (e) { alert(e?.response?.data?.detail || "Nahrání selhalo."); }
  };

  const unassign = async (slot) => {
    const existing = bySlot(slot.target_type, slot.target_slug);
    if (existing.length === 0) return;
    if (!window.confirm(`Odstranit fotografii ze slotu „${slot.label}"?`)) return;
    for (const p of existing) { await adminApi.delete(`/photos/${p.id}`); }
    await onChanged();
  };

  // Group slots by section for readability
  const sections = {};
  for (const s of IMAGE_SLOTS) { (sections[s.section] = sections[s.section] || []).push(s); }

  return (
    <div className="bg-ivory border border-charcoal/10 p-5 md:p-6 mb-8" data-testid="slots-panel">
      <div className="flex items-center gap-2 mb-1">
        <ImageIcon size={18} className="text-burgundy" />
        <h2 className="font-serif text-xl">Sloty pro fotografie</h2>
      </div>
      <p className="text-xs text-charcoal/60 mb-5 max-w-2xl">Každá pozice na webu má vlastní slot. Nahrání sem přepíše fotografii přímo v dané sekci; smazání odebere pouze přiřazení pro tuto pozici.</p>

      {Object.keys(sections).map((sec) => (
        <div key={sec} className="mb-6">
          <div className="text-[11px] tracking-[.24em] uppercase text-charcoal/60 mb-3">{sec}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sections[sec].map((slot) => {
              const items = bySlot(slot.target_type, slot.target_slug);
              const current = items[0];
              return (
                <div key={slot.key} className="bg-white border border-charcoal/10 p-3" data-testid={`slot-${slot.key}`}>
                  <div className="aspect-[4/3] bg-cream mb-2 relative overflow-hidden">
                    {current ? (
                      <img src={fileUrl(current.id)} alt={current.alt || slot.label} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-charcoal/30 text-xs italic">Bez fotografie</div>
                    )}
                  </div>
                  <div className="text-xs font-medium leading-snug">{slot.label}</div>
                  <div className="text-[10px] text-charcoal/50 mt-0.5 truncate">{slot.key}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <label className="inline-block bg-burgundy hover:bg-burgundy-hover text-ivory text-xs px-3 py-1.5 cursor-pointer" data-testid={`slot-upload-${slot.key}`}>
                      {current ? "Nahradit" : "Nahrát"}
                      <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => upload(slot, e.target.files?.[0])} />
                    </label>
                    {current && (
                      <button onClick={() => unassign(slot)} className="text-xs text-charcoal/60 hover:text-burgundy inline-flex items-center gap-1" data-testid={`slot-remove-${slot.key}`}>
                        <Trash2 size={12} /> Odstranit
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------- Wines ----------------------
const WINE_FIELDS = [
  { key: "name", label: "Název", required: true },
  { key: "vintage", label: "Ročník" },
  { key: "sweetness", label: "Cukernatost (nepovinné)" },
  { key: "category", label: "Kategorie" },
  { key: "alcohol", label: "Alkohol" },
  { key: "region", label: "Vinařská oblast" },
  { key: "volume", label: "Objem" },
  { key: "price", label: "Cena (Kč)", type: "number", required: true },
  { key: "alt_main", label: "Alt text – hlavní fotografie" },
  { key: "alt_second", label: "Alt text – druhá fotografie" },
];

function WinesTab() {
  const [wines, setWines] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    const [w, p] = await Promise.all([adminApi.get("/wines"), adminApi.get("/photos")]);
    setWines(w.data.wines || []);
    setPhotos(p.data.photos || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const photoFor = (type, slug) => photos.filter((p) => p.target_type === type && p.target_slug === slug)
    .sort((a, b) => (a.order || 0) - (b.order || 0))[0];

  return (
    <section className="py-8">
      <h1 className="font-serif text-3xl mb-2">Vína</h1>
      <p className="text-sm text-charcoal/60 mb-6 max-w-2xl">Údaje se zobrazují na stránce Naše vína. Prázdná pole (např. cukernatost) se na webu nezobrazí.</p>
      <div className="grid gap-2">
        {wines.map((w) => {
          const main = photoFor("wine", w.slug);
          return (
            <div key={w.slug} className="bg-ivory border border-charcoal/10 p-3 flex items-center gap-4" data-testid={`admin-wine-${w.slug}`}>
              <div className="w-14 h-16 bg-cream shrink-0 overflow-hidden">
                {main && <img src={fileUrl(main.id)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{w.name} {!w.published && <span className="text-xs text-charcoal/50">(skryté)</span>}</div>
                <div className="text-xs text-charcoal/60">{[w.vintage, w.sweetness, w.alcohol, `${w.price} Kč`].filter(Boolean).join(" · ")}</div>
              </div>
              <button onClick={() => setEditing(w)} className="text-sm border border-charcoal/20 px-3 py-1.5 hover:bg-cream inline-flex items-center gap-1.5" data-testid={`admin-wine-edit-${w.slug}`}>
                <Pencil size={13} /> Upravit
              </button>
            </div>
          );
        })}
      </div>
      {editing && (
        <WineEditor wine={editing} photoFor={photoFor} onClose={() => setEditing(null)} onChanged={load}
          onSaved={async () => { setEditing(null); await load(); }} />
      )}
    </section>
  );
}

function WineEditor({ wine, photoFor, onClose, onChanged, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(WINE_FIELDS.map((f) => [f.key, wine[f.key] ?? ""])));
  const [published, setPublished] = useState(wine.published !== false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      await adminApi.put(`/wines/${wine.slug}`, { ...form, price: Number(form.price) || 0, order: wine.order || 0, published });
      await onSaved();
    } catch (e2) { setErr(e2?.response?.data?.detail?.toString?.() || "Uložení selhalo."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60" onClick={onClose} data-testid="wine-editor">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-ivory w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Upravit víno</h2>
          <button type="button" onClick={onClose} aria-label="Zavřít" className="p-1 hover:bg-cream"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <WinePhotoSlot label="Hlavní fotografie" type="wine" wine={wine} current={photoFor("wine", wine.slug)} alt={form.alt_main} onChanged={onChanged} />
          <WinePhotoSlot label="Druhá fotografie" type="wine-detail" wine={wine} current={photoFor("wine-detail", wine.slug)} alt={form.alt_second} onChanged={onChanged} />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {WINE_FIELDS.map((f) => (
            <label key={f.key}>
              <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">{f.label}</span>
              <input type={f.type || "text"} required={f.required} min={f.type === "number" ? 0 : undefined} value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="w-full border border-charcoal/25 px-3 py-2.5 text-sm bg-white" data-testid={`wine-field-${f.key}`} />
            </label>
          ))}
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} data-testid="wine-field-published" /> Zobrazit na webu
        </label>
        {err && <div className="mt-3 text-sm text-red-700">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-charcoal/25 px-4 py-2 text-sm">Zrušit</button>
          <button disabled={saving} type="submit" className="bg-burgundy hover:bg-burgundy-hover text-ivory px-5 py-2 text-sm" data-testid="wine-editor-save">
            {saving ? "Ukládám…" : "Uložit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function WinePhotoSlot({ label, type, wine, current, alt, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_type", type);
      fd.append("target_slug", wine.slug);
      fd.append("alt", alt || wine.name);
      fd.append("order", "0");
      await adminApi.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (current) await adminApi.delete(`/photos/${current.id}`);
      await onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Nahrání selhalo."); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!current || !window.confirm(`Odstranit ${label.toLowerCase()}?`)) return;
    await adminApi.delete(`/photos/${current.id}`);
    await onChanged();
  };
  return (
    <div className="bg-white border border-charcoal/10 p-3" data-testid={`wine-photo-${type}`}>
      <div className="aspect-[4/3] bg-cream mb-2 overflow-hidden">
        {current ? <img src={fileUrl(current.id)} alt="" className="w-full h-full object-cover" /> :
          <div className="w-full h-full flex items-center justify-center text-charcoal/30 text-xs italic">Bez fotografie</div>}
      </div>
      <div className="text-xs font-medium">{label}</div>
      <div className="mt-2 flex items-center justify-between">
        <label className="inline-block bg-burgundy hover:bg-burgundy-hover text-ivory text-xs px-3 py-1.5 cursor-pointer">
          {busy ? "Nahrávám…" : current ? "Nahradit" : "Nahrát"}
          <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid={`wine-photo-input-${type}`} />
        </label>
        {current && <button type="button" onClick={remove} className="text-xs text-charcoal/60 hover:text-burgundy inline-flex items-center gap-1"><Trash2 size={12} /> Odstranit</button>}
      </div>
    </div>
  );
}
